import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosMaterialMayorAPI, bomberosCompaniaAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
    Truck,
    Plus,
    Search,
    ArrowLeft,
    AlertTriangle,
    CheckCircle,
    Wrench,
    XCircle,
    Eye,
    Edit,
    Calendar,
    Droplets,
    Trash2,
} from 'lucide-react';

export default function BomberosMaterialMayorPage() {
    const navigate = useNavigate();
    const { tienePermiso } = useBomberosPermisos();
    const [loading, setLoading] = useState(true);
    const [materiales, setMateriales] = useState([]);
    const [companias, setCompanias] = useState([]);
    const [filteredMateriales, setFilteredMateriales] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCompania, setFilterCompania] = useState('all');
    const [filterTipo, setFilterTipo] = useState('all');
    const [filterEstado, setFilterEstado] = useState('all');

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        filterMateriales();
    }, [materiales, searchTerm, filterCompania, filterTipo, filterEstado]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [materialesRes, companiasRes] = await Promise.all([
                bomberosMaterialMayorAPI.getAll(),
                bomberosCompaniaAPI.getAll(),
            ]);
            setMateriales(materialesRes.data);
            setCompanias(companiasRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const filterMateriales = () => {
        let filtered = [...materiales];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(m =>
                m.codigo?.toLowerCase().includes(term) ||
                m.patente?.toLowerCase().includes(term) ||
                m.marca?.toLowerCase().includes(term) ||
                m.modelo?.toLowerCase().includes(term)
            );
        }

        if (filterCompania !== 'all') {
            filtered = filtered.filter(m => m.compania_id === filterCompania);
        }

        if (filterTipo !== 'all') {
            filtered = filtered.filter(m => m.tipo === filterTipo);
        }

        if (filterEstado !== 'all') {
            filtered = filtered.filter(m => m.estado_operativo === filterEstado);
        }

        setFilteredMateriales(filtered);
    };

    const handleEliminar = async (material) => {
        if (!window.confirm(`¿Está seguro de eliminar el vehículo ${material.patente}?`)) return;
        
        try {
            await bomberosMaterialMayorAPI.delete(material.id);
            toast.success('Vehículo eliminado correctamente');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar');
        }
    };

    const getEstadoIcon = (estado) => {
        switch (estado) {
            case 'operativo': return <CheckCircle className="h-4 w-4 text-green-500" />;
            case 'mantenimiento': return <Wrench className="h-4 w-4 text-yellow-500" />;
            case 'fuera_servicio': return <XCircle className="h-4 w-4 text-red-500" />;
            default: return null;
        }
    };

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'operativo': return 'bg-green-100 text-green-700 border-green-300';
            case 'mantenimiento': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
            case 'fuera_servicio': return 'bg-red-100 text-red-700 border-red-300';
            default: return 'bg-gray-100 text-gray-700 border-gray-300';
        }
    };

    const getTipoLabel = (tipo) => {
        const labels = {
            carro_bomba: 'Carro Bomba',
            aljibe: 'Aljibe',
            escalera: 'Escalera',
            rescate: 'Rescate',
            hazmat: 'Hazmat',
        };
        return labels[tipo] || tipo;
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-48" />)}
                </div>
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Truck className="h-6 w-6" />
                            Material Mayor
                        </h1>
                        <p className="text-muted-foreground">{filteredMateriales.length} vehículos registrados</p>
                    </div>
                </div>
                {tienePermiso('crear_material_mayor') && (
                    <Button onClick={() => navigate('/bomberos/material-mayor/nuevo')} data-testid="nuevo-vehiculo-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Nuevo Vehículo
                    </Button>
                )}
            </div>

            {/* Filters */}
            <Card className="border-2">
                <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por código, patente, marca..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={filterCompania} onValueChange={setFilterCompania}>
                            <SelectTrigger className="w-full md:w-48" data-testid="filter-compania">
                                <SelectValue placeholder="Compañía" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas las Compañías</SelectItem>
                                {companias.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterTipo} onValueChange={setFilterTipo}>
                            <SelectTrigger className="w-full md:w-40" data-testid="filter-tipo">
                                <SelectValue placeholder="Tipo" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los Tipos</SelectItem>
                                <SelectItem value="carro_bomba">Carro Bomba</SelectItem>
                                <SelectItem value="aljibe">Aljibe</SelectItem>
                                <SelectItem value="escalera">Escalera</SelectItem>
                                <SelectItem value="rescate">Rescate</SelectItem>
                                <SelectItem value="hazmat">Hazmat</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterEstado} onValueChange={setFilterEstado}>
                            <SelectTrigger className="w-full md:w-40" data-testid="filter-estado">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="operativo">Operativo</SelectItem>
                                <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                                <SelectItem value="fuera_servicio">Fuera de Servicio</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border border-green-200 bg-green-50 dark:bg-green-950">
                    <CardContent className="pt-4 pb-4 text-center">
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-green-700">{materiales.filter(m => m.estado_operativo === 'operativo').length}</p>
                        <p className="text-xs text-green-600">Operativos</p>
                    </CardContent>
                </Card>
                <Card className="border border-yellow-200 bg-yellow-50 dark:bg-yellow-950">
                    <CardContent className="pt-4 pb-4 text-center">
                        <Wrench className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-yellow-700">{materiales.filter(m => m.estado_operativo === 'mantenimiento').length}</p>
                        <p className="text-xs text-yellow-600">En Mantenimiento</p>
                    </CardContent>
                </Card>
                <Card className="border border-red-200 bg-red-50 dark:bg-red-950">
                    <CardContent className="pt-4 pb-4 text-center">
                        <XCircle className="h-6 w-6 text-red-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-red-700">{materiales.filter(m => m.estado_operativo === 'fuera_servicio').length}</p>
                        <p className="text-xs text-red-600">Fuera de Servicio</p>
                    </CardContent>
                </Card>
            </div>

            {/* Grid de Vehículos */}
            {filteredMateriales.length === 0 ? (
                <Card className="border-2">
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No se encontraron vehículos con los filtros aplicados</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredMateriales.map((material) => (
                        <Card
                            key={material.id}
                            className="border-2 hover:border-primary transition-colors cursor-pointer"
                            onClick={() => navigate(`/bomberos/material-mayor/${material.id}`)}
                            data-testid={`vehiculo-${material.codigo}`}
                        >
                            <CardContent className="pt-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h3 className="font-bold text-lg">{material.codigo}</h3>
                                        <p className="text-sm text-muted-foreground">{getTipoLabel(material.tipo)}</p>
                                    </div>
                                    <Badge className={`${getEstadoColor(material.estado_operativo)} flex items-center gap-1`}>
                                        {getEstadoIcon(material.estado_operativo)}
                                        <span className="capitalize">{material.estado_operativo.replace('_', ' ')}</span>
                                    </Badge>
                                </div>

                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Vehículo:</span>
                                        <span className="font-medium">{material.marca} {material.modelo} ({material.año})</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Patente:</span>
                                        <span className="font-mono font-medium">{material.patente}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Compañía:</span>
                                        <span className="font-medium truncate max-w-32">{material.compania_nombre || '-'}</span>
                                    </div>
                                    {material.capacidad_agua > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-muted-foreground flex items-center gap-1">
                                                <Droplets className="h-3 w-3" /> Agua:
                                            </span>
                                            <span className="font-medium">{material.capacidad_agua.toLocaleString()} L</span>
                                        </div>
                                    )}
                                </div>

                                {/* Alertas */}
                                {material.alertas && material.alertas.length > 0 && (
                                    <div className="mt-4 p-2 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                                        {material.alertas.map((alerta, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-xs text-yellow-700 dark:text-yellow-400">
                                                <AlertTriangle className="h-3 w-3" />
                                                <span>{alerta}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Documentación */}
                                <div className="mt-4 pt-4 border-t flex justify-between text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        <span>Rev: {material.fecha_revision_tecnica || 'Sin fecha'}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/bomberos/material-mayor/${material.id}`);
                                            }}
                                        >
                                            <Eye className="h-4 w-4" />
                                        </Button>
                                        {tienePermiso('editar_material_mayor') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/bomberos/material-mayor/${material.id}/editar`);
                                                }}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                        )}
                                        {tienePermiso('eliminar_material_mayor') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-destructive hover:text-destructive"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEliminar(material);
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
        </>
    );
}
